import React, { useEffect, PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useDropzone } from 'react-dropzone';

import Icon from './Icon';

import ThemeIcons from '../../theme/Icons';

const ImageManager = ({
  disabled,
  multiple,
  isMobileView,
  value,

  onDrop,
  onFocus,
  onDelete,
  onLoadCompleted,

  maxWidth,
  maxHeight,

  fontColor,
  borderRadius,
  borderWidth,
  borderColor,
  padding,
  backgroundColor,
  primaryColor,
  dangerColor,
  secondaryColor

}) => {

  return !value || value.length < 1 ? (
    <ImageManagerDropzone
      {...{
        disabled,
        multiple,
        onDrop,
        onFocus,
        onLoadCompleted,

        fontColor,
        borderColor,
        borderWidth,
        borderRadius,
        backgroundColor,
        primaryColor,
        secondaryColor

      }}
    />
  ) : (
    <ImageManagerViewer
      {...{
        isMobileView,
        disabled,
        multiple,
        value: Array.isArray(value) ? value : [value],

        maxWidth,
        maxHeight,
        fontColor,
        borderColor,
        borderWidth,
        borderRadius,
        backgroundColor,
        primaryColor,
        secondaryColor,
        dangerColor,
        padding,

        onDelete,
        onDrop,
        onFocus,
        onLoadCompleted
      }}
    />
  );

};

const StyledImageViewerWrapper = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  border: 1px solid transparent;

  @media only screen 
    and (min-device-width: 375px) 
    and (max-device-width: 812px) 
    and (-webkit-min-device-pixel-ratio: 3)
    and (orientation: portrait) { 
      overflow-y: auto !important;
  }

  #image-manager-action-toolbar {
    display: ${(props) => (!props.autoShow ? 'none' : 'block')};
  }

  &:hover {

    #image-manager-action-toolbar {
      display: block;
    }

    border: 1px solid ${(props) => (props.disabled ? 'transparent' : props.theme.borderColor)};
    border-radius: ${(props) => props.theme.borderRadius}px;

  }

`;

const StyledImageViewerScroller = styled.div`
  height: 100%;
  overflow: hidden;
  ${(props) => {

    if (!props.multiple) {
      return '';
    } else {
      return `  
        scrollbar-gutter: stable;
        margin-right: -17px;
        overflow-y: auto;
      `;
    }

  }}

`;

const StyledImageViewerInternalWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
`;

const StyledImageViewer = styled.div`
  position: relative;
  border: ${(props) => props.border};
  border-radius: ${(props) => props.borderRadius}px;
  width: ${(props) => props.width || (props.autoShow ? '97%' : '99%')};
  ${(props) => (props.height ? `height: ${props.height};` : '')}
  margin: ${(props) => (props.padding ? `${props.padding}px` : '0px')};
  box-sizing: border-box;
  overflow: hidden;

  #image-viewer-delete-icon {
    visibility: ${(props) => (!props.autoShow ? 'hidden' : 'visible')};
  }

  :hover {

    #image-viewer-delete-icon {
      visibility: visible;
    }

  }
`;

const StyledImageViewerImage = styled.img`
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const StyledImageViewerIcon = styled.span`
  position: absolute;
  top: 4px;
  right: 4px;
  border-radius: 100px;
  border: 1px solid transparent;
  background-color: ${(props) => props.theme.backgroundColor};
`;

const StyledImageViewerActionWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
`;

export class ImageManagerViewer extends PureComponent {

  static propTypes = {

    disabled: PropTypes.bool,
    multiple: PropTypes.bool,
    onLoadCompleted: PropTypes.func,
    onDelete: PropTypes.func,
    onDrop: PropTypes.func,
    onFocus: PropTypes.func,
    value: PropTypes.array,

    borderWidth: PropTypes.number,
    borderColor: PropTypes.string,
    borderRadius: PropTypes.number,
    padding: PropTypes.number,

    maxWidth: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]), //We make this a string so we can use '200px' or '33%'
    maxHeight: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]), //We make this a string so we can use '200px' or '33%'
  }

  constructor(props) {
    super(props);
    this.state = { imageLoadUpdated: false };
    this.loadedImages = {};
  }

  componentDidUpdate(prevProps, prevState) {

    const { value, onLoadCompleted } = this.props;
    const { imageLoadUpdated } = this.state;

    if (prevState.imageLoadUpdated !== imageLoadUpdated && onLoadCompleted && Object.keys(this.loadedImages).length === value.length) onLoadCompleted();

  }

  render() {

    const {

      disabled,
      multiple,
      value,
      isMobileView,

      onDelete,
      onDrop,
      onFocus,

      //Styles
      maxWidth,
      maxHeight,
      borderRadius,
      borderWidth,
      borderColor,
      padding,
      fontColor,
      primaryColor,
      dangerColor,
      backgroundColor

    } = this.props;

    const theme = {
      borderColor,
      borderRadius,
      borderWidth,
      fontColor,
      backgroundColor,
      primaryColor,
      dangerColor
    };

    /**
     * We join width and color to a single border style because chrome displays
     * the border as a One tone color if you use border-color and border-width
     * indvidually.
     */
    const border = `${borderWidth}px solid ${borderColor}`;

    const imgStyles = {
      width: multiple ? maxWidth : '100%',
      height: multiple ? maxHeight : '',
      borderRadius,
      padding,
      border,
    };

    const imageNodes = value.map((imgData = {}, i) => {

      const { url } = imgData;

      return (
        <StyledImageViewer
          {...imgStyles}
          key={i}
          autoShow={isMobileView}
        >
          <StyledImageViewerImage
            data-testid={`${i}-image-field`}
            src={url}
            onLoad={() => this.handleImageLoad(i)}
            onError={() => this.handleImageLoad(i)}
          />
          {disabled ? null : (
            <StyledImageViewerIcon theme={theme} id="image-viewer-delete-icon">
              <Icon
                size="lg"
                icon={ThemeIcons.settings.deleteSolid}
                color={dangerColor}
                hoverColor={dangerColor}
                onClick={() => {
                  onFocus();
                  onDelete(i);
                }}
              />
            </StyledImageViewerIcon>
          )}
        </StyledImageViewer>
      );

    });

    return (
      <StyledImageViewerWrapper theme={theme} disabled={disabled} autoShow={isMobileView}>
        <StyledImageViewerScroller {...{ multiple }}>
          <StyledImageViewerInternalWrapper>
            {imageNodes}
          </StyledImageViewerInternalWrapper>
        </StyledImageViewerScroller>
        <StyledImageViewerActionWrapper>
          <ActionToolbar {...{ multiple, onDrop, onFocus, disabled, isMobileView, ...theme }} />
        </StyledImageViewerActionWrapper>
      </StyledImageViewerWrapper>
    );

  }

  handleImageLoad = (i) => {

    this.loadedImages[i] = true;

    /**
     * We do this timestamp update to trigger the componentDidUpdate check.
     */
    this.setState({ imageLoadUpdated: new Date().getTime() });

  }

}

const StyledActionToolbarWrapper = styled.div`
  position: absolute;
  bottom: 6px;
  right: 6px;
  display: flex;
  cursor:pointer;
  z-index: 1000;
`;

const StyledAction = styled.button`
  display: flex;
  flex-direction: row;
  background: ${(props) => props.theme.backgroundColor};;
  border: 1px solid ${(props) => props.theme.borderColor};
  border-radius: ${(props) => props.theme.borderRadius}px;
  color: ${(props) => props.theme.fontColor};
  font-size: 12px;
  padding: 6px 12px;
  box-shadow: 0px 4px 6px rgba(19, 20, 26, 0.12);
  transition: all 0.15s ease-in-out;

  svg {
    color: ${(props) => props.theme.fontColor};
  }

  &:hover {
    cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')}; 
    border: 1px solid ${(props) => props.theme.primaryColor};
    color: ${(props) => props.theme.primaryColor};
    svg {
      color: ${(props) => props.theme.primaryColor};
    }
  }
`;

const StyledActionText = styled.div`
  margin-left: 4px;
  z-index: 1000;
`;

export const ActionToolbar = ({
  disabled,
  onDrop,
  onFocus,
  multiple,

  primaryColor,
  fontColor,
  borderColor,
  borderRadius,
  backgroundColor
}) => {

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'image/jpeg': ['.jpg', '.jpeg', '.JPG', '.JPEG'],
      'image/png': ['.png', '.PNG'],
    },
    onDrop,
    multiple
  });

  if (disabled) return null;

  return (
    <StyledActionToolbarWrapper id="image-manager-action-toolbar">
      <StyledAction
        theme={{
          primaryColor,
          fontColor,
          borderColor,
          borderRadius,
          backgroundColor
        }}
        {...getRootProps({
          onClick: () => onFocus()
        })}
      >
        <input {...getInputProps()} />
        <Icon
          icon={ThemeIcons.settings.upload}
          size="1x"
        />
        <StyledActionText>Upload</StyledActionText>
      </StyledAction>
    </StyledActionToolbarWrapper>
  );

};

const StyledImageDropzoneWrapper = styled.div`
  display: flex;
  align-items:center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: ${(props) => props.theme.backgroundColor};
  border: ${(props) => props.theme.borderWidth}px dashed ${(props) => props.theme.borderColor};
  border-radius: ${(props) => props.theme.borderRadius}px;
  box-sizing: border-box;
  overflow: hidden;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  color: ${(props) => props.theme.fontColor};
  transition: all .15s ease-in-out;

  svg {
    color: ${(props) => props.theme.fontColor};
    font-size: 16px;
  }

  &:hover {
    cursor: pointer;
    color: ${(props) => props.theme.primaryColor};
    border: ${(props) => props.theme.borderWidth}px dashed ${(props) => props.theme.primaryColor} !important;
    border-radius: ${(props) => props.theme.borderRadius}px;

    svg {
      color: ${(props) => props.theme.primaryColor};
      font-size: 16px;
    }
  }

`;

export const ImageManagerDropzone = ({

  fontColor,
  borderColor,
  borderRadius,
  borderWidth,
  primaryColor,
  secondaryColor,
  backgroundColor,

  multiple,
  disabled,
  onDrop,
  onFocus,
  onLoadCompleted

}) => {

  const theme = {
    fontColor,
    backgroundColor: backgroundColor || secondaryColor,
    borderColor,
    borderRadius,
    borderWidth,
    primaryColor,
  };

  /**
   * Immediately fire onLoadCompleted for placeholder because
   * there are no images loading to wait for.
   */
  useEffect(() => {
    onLoadCompleted();
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'image/jpeg': ['.jpg', '.jpeg', '.JPG', '.JPEG'],
      'image/png': ['.png', '.PNG'],
    },
    onDrop: (files) => {
      if (multiple) onDrop(files);
      else onDrop([files[0]]); //Only use first image if multiple is false
    },
    noClick: disabled
  });

  return disabled ? (
    <StyledImageDropzoneWrapper theme={theme} disabled>
      <Icon icon={ThemeIcons.fields.upload} />
    </StyledImageDropzoneWrapper>
  ) : (
    <StyledImageDropzoneWrapper
      theme={theme}
      {...getRootProps({
        onClick: () => {
          onFocus();
        },
        onDrop: () => {
          onFocus();
        }
      })}
    >
      <input {...getInputProps()} />
      <Icon icon={ThemeIcons.settings.upload} />
    </StyledImageDropzoneWrapper>
  );

};

export default ImageManager;
